import { sleep } from '@ardoq/common-helpers';
import { logWarn } from '@ardoq/logging';
import * as profiling from '@ardoq/profiling';
import {
  type CanvasToSvgContext,
  createCanvasToSvgContext,
  createRecordingContext,
  escapeXmlAttr,
  escapeXmlText,
} from './canvasToSvg';
import { EXPORT_MARGIN_PX } from './consts';
import { getPendingImages } from './pendingImages';
import { getClonedDOM } from './exportUtil';
import {
  getFontFaceRules,
  getStyles,
  serializeAndWrapInSVG,
} from './htmlExport';
import type { ExportTrackingData, GetStyleSheetsFn } from './types';

/** How many render passes `renderUntilImagesSettle` will run before giving up. */
export const MAX_IMAGE_LOAD_PASSES = 12;
const IMAGE_LOAD_POLL_MS = 100;
/**
 * A component image is fetched from a customer asset host, and `decode()` on one
 * whose fetch stalls never settles — so awaiting it unbounded would leave the
 * export with no pass limit and no way to fail.
 */
const IMAGE_DECODE_WAIT_MS = 1_000;
/** gap between the graph content and the legend, in export pixels */
const LEGEND_GAP_PX = 32;

export type RecordedSvgOptions = {
  legendElement?: HTMLElement;
  title?: string;
  getStyleSheets: GetStyleSheetsFn;
  transaction: profiling.Transaction;
  mutableTrackingData: ExportTrackingData;
};

/** the size to fall back on when a recording drew no content at all */
export type FallbackSize = { width: number; height: number };

/**
 * What a render pass produced: the images it drew, and whether the render itself
 * is finished — a canvas renderer caught mid-animation draws an interpolated
 * frame, which an export must not capture. `null` when the pass hit content it
 * cannot render at all.
 */
export type RenderPassResult = {
  images: Iterable<HTMLImageElement>;
  isComplete: boolean;
} | null;

export type ImageSettleOutcome = 'settled' | 'unsettled' | 'aborted';

/**
 * Canvas renderers draw images synchronously from a cache and get blank pixels
 * for the ones still loading, so a single pass is not enough: run `renderPass`
 * until it reports a complete render whose images have all loaded.
 *
 * A pass that drew nothing is never taken as settled on its own. The renderers
 * *skip* an image that has not loaded — the first pass only kicks the load off —
 * so "no images drawn" is indistinguishable from "images still in flight", which
 * is exactly the state of the first pass for nodes that were never on screen.
 * Settling needs a pass whose image count matches the one before it.
 *
 * Images a renderer requested but could not draw are invisible to that count, so
 * a pass with any of them outstanding is never settled either. That set is
 * app-wide, so an image loading for another view holds this loop to its pass
 * limit; the warning it ends on says which of the two it gave up on.
 */
export const renderUntilImagesSettle = async (
  renderPass: () => RenderPassResult
): Promise<ImageSettleOutcome> => {
  const counts = { previous: -1 };
  const lastPass = { drewEverythingItCould: false };

  for (let pass = 0; pass < MAX_IMAGE_LOAD_PASSES; pass++) {
    const drawn = renderPass();
    if (!drawn) {
      return 'aborted';
    }

    const images = [...drawn.images];
    const pending = getPendingImages();
    const drewEverythingItCould =
      drawn.isComplete &&
      images.length === counts.previous &&
      images.every(image => image.complete);
    if (drewEverythingItCould && pending.length === 0) {
      return 'settled';
    }

    lastPass.drewEverythingItCould = drewEverythingItCould;
    counts.previous = images.length;
    await Promise.race([
      Promise.all(
        [...images, ...pending].map(image =>
          image.decode().catch(() => undefined)
        )
      ),
      sleep(IMAGE_DECODE_WAIT_MS),
    ]);
    await sleep(IMAGE_LOAD_POLL_MS);
  }

  logWarn(
    new Error(
      lastPass.drewEverythingItCould
        ? 'Direct export: the render finished and every image it drew had loaded, but image requests were still outstanding at the pass limit; those may belong to this diagram, since a renderer skips an image until it loads, or to another view in the app'
        : 'Direct export: the render did not settle within the pass limit, either waiting on images or on a render that kept reporting itself unfinished; the export may be missing images or show a mid-animation frame'
    )
  );
  return 'unsettled';
};

/**
 * What one recording pass achieved: whether the content could be recorded at all
 * (`renderable: false` tells the caller to fall back to another export path), and
 * whether the frame it drew is final rather than mid-animation.
 */
export type RecordingPassOutcome = {
  renderable: boolean;
  isComplete: boolean;
};

/**
 * Runs `render` against a {@link CanvasToSvgContext} recorder disguised as a 2D
 * canvas context, repeating until it reports a complete frame whose images have
 * loaded.
 */
export const recordSvg = async (
  render: (ctx: CanvasRenderingContext2D) => RecordingPassOutcome
): Promise<{ recorder: CanvasToSvgContext; renderable: boolean }> => {
  await document.fonts.ready;

  const passes = { recorder: createCanvasToSvgContext(), renderable: true };

  await renderUntilImagesSettle(() => {
    const { context, recorder } = createRecordingContext();
    passes.recorder = recorder;
    const { renderable, isComplete } = render(context);
    passes.renderable = renderable;
    return renderable ? { images: recorder.drawnImages, isComplete } : null;
  });

  return passes;
};

/** @font-face CSS with the app's fonts inlined as data URLs, so text (incl. FontAwesome icons) renders standalone. */
const getInlinedFontStyle = async (
  getStyleSheets: GetStyleSheetsFn
): Promise<string> => {
  try {
    const sheets = await getStyleSheets();
    const fontRules = await getFontFaceRules(sheets);
    const css = fontRules.map(rule => rule.cssText).join('');
    return css ? `<style>${css}</style>` : '';
  } catch (error) {
    logWarn(error as Error, 'Failed to inline fonts for SVG export');
    return '';
  }
};

/** fetch a resource and return its original bytes as a data URL (no re-encoding). */
const fetchAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logWarn(error as Error, 'Failed to inline image for SVG export');
    return null;
  }
};

/** replace remote (http) image references with inlined data URLs so the SVG is self-contained. */
const inlineRemoteImages = async (
  svgBody: string,
  images: HTMLImageElement[]
): Promise<string> => {
  const remoteSources = [
    ...new Set(
      images.map(image => image.src).filter(src => /^https?:/i.test(src))
    ),
  ];
  const inlined = await Promise.all(
    remoteSources.map(
      async source => [source, await fetchAsDataUrl(source)] as const
    )
  );
  return [...inlined]
    .sort(([a], [b]) => b.length - a.length)
    .reduce(
      (body, [source, dataUrl]) =>
        dataUrl
          ? body.split(escapeXmlAttr(source)).join(escapeXmlAttr(dataUrl))
          : body,
      svgBody
    );
};

/** render the view legend DOM into a positioned SVG fragment (foreign object). */
const renderLegendMarkup = async (
  legendElement: HTMLElement,
  x: number,
  y: number,
  { getStyleSheets, transaction, mutableTrackingData }: RecordedSvgOptions
): Promise<{ markup: string; width: number; height: number } | null> => {
  try {
    legendElement.classList.add('forExport');
    const clonedLegend = await getClonedDOM(
      legendElement,
      'viewLegend',
      mutableTrackingData,
      transaction
    );
    const width = legendElement.scrollWidth;
    const height = legendElement.scrollHeight;
    legendElement.classList.remove('forExport');
    clonedLegend.style.setProperty('position', 'static');
    clonedLegend.style.setProperty('width', `${width}px`);
    clonedLegend.style.setProperty('height', `${height}px`);
    const styles = await getStyles(
      clonedLegend,
      getStyleSheets,
      '',
      transaction
    );
    const markup = serializeAndWrapInSVG(
      transaction,
      clonedLegend,
      width,
      height,
      styles,
      [x, y]
    );
    return { markup, width, height };
  } catch (error) {
    legendElement.classList.remove('forExport');
    logWarn(error as Error, 'Failed to render legend for SVG export');
    return null;
  }
};

/**
 * Assembles a standalone SVG document from a finished {@link recordSvg}
 * recording: fonts and remote images are inlined so the file stands on its own,
 * the legend is appended to the right of the content, and the canvas is sized
 * tightly to what was actually drawn.
 */
export const composeRecordedSvgDocument = async (
  recorder: CanvasToSvgContext,
  fallbackSize: FallbackSize,
  options: RecordedSvgOptions
): Promise<string> => {
  const bounds = recorder.getContentBounds();
  const hasContent = Number.isFinite(bounds.minX);
  const contentMinX = hasContent ? bounds.minX : 0;
  const contentMinY = hasContent ? bounds.minY : 0;
  const contentMaxX = hasContent ? bounds.maxX : fallbackSize.width;
  const contentMaxY = hasContent ? bounds.maxY : fallbackSize.height;

  const fontStyle = await getInlinedFontStyle(options.getStyleSheets);
  const serialized = recorder.serialize(fontStyle);
  const body = await inlineRemoteImages(serialized, [...recorder.drawnImages]);

  const legend = options.legendElement
    ? await renderLegendMarkup(
        options.legendElement,
        contentMaxX + LEGEND_GAP_PX,
        contentMinY,
        options
      )
    : null;

  const rightExtent = legend
    ? contentMaxX + LEGEND_GAP_PX + legend.width
    : contentMaxX;
  const bottomExtent = legend
    ? Math.max(contentMaxY, contentMinY + legend.height)
    : contentMaxY;

  const minX = contentMinX - EXPORT_MARGIN_PX;
  const minY = contentMinY - EXPORT_MARGIN_PX;
  const width = Math.ceil(rightExtent - minX + EXPORT_MARGIN_PX);
  const height = Math.ceil(bottomExtent - minY + EXPORT_MARGIN_PX);

  const roleAttr = options.title ? ' role="img"' : '';
  const titleMarkup = options.title
    ? `<title>${escapeXmlText(options.title)}</title>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="${minX} ${minY} ${width} ${height}"${roleAttr}>${titleMarkup}${body}${legend?.markup ?? ''}</svg>`;
};
